import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Helmet } from "react-helmet";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';

import Download from 'components/Download';
import ChatModal from 'components/ChatModal';
import ChatButton from 'components/ChatModal/ChatButton';
import ChatIframe from 'components/ChatModal/ChatIframe';
import { AppContext } from 'context/AppContext';
import { Campaign, Task as TaskModel } from 'models/CommonType';
import { useCampaign } from 'services/CampaignService';
import { useEnrollment } from 'services/EnrollmentService';
import { useSubmissions, useTask } from 'services/TaskService';
import { Submission } from 'services/types/TaskTypes';
import { urlify } from "utils/helpers";

import SubmissionList from './components/SubmissionList';
import SubmissionBanner from './components/SubmissionBanner';
import TaskDetailSkeleton from './components/TaskDetailSkeleton';
import FadeInWrapper from './components/FadeInWrapper';
import './TaskDetail.css';

const Task: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { authentication, commonData } = useContext(AppContext);
  const { authKey } = authentication;

  const taskId = searchParams.get("id") || '';
  const { data: taskData, isError: taskError, isLoading: isTaskLoading, mutate: taskMutate } = useTask(taskId, authKey);
  const { data: submissionsData, isLoading: isSubmissionsLoading, mutate: submissionsMutate } = useSubmissions(taskId, authKey);
  const task = taskData as TaskModel;
  const submissions = submissionsData as Submission[];
  const availableType = task?.task_type === 'A_TEXT' ||
    task?.task_type === 'M_TEXT' ||
    task?.task_type === 'A_COMMENT' ||
    task?.task_type === 'A_LIKE' ||
    task?.task_type === 'M_PHOTO';

  useEffect(() => {
    if (!isEmpty(taskError)) {
      navigate('/');
    }
  }, [taskError, navigate]);

  const [imageError, setImageError] = useState(false);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);

  // fetch campaign info
  const campaignId = useMemo(() => {
    if (isEmpty(task)) return '';

    const url = new URL(task.campaign);
    return url.pathname.split('/').filter(e => e).pop() ?? '';
  }, [task]);
  const { data: campaignData } = useCampaign(campaignId, authKey);
  const campaign = campaignData as Campaign;

  // fetch enrollment info
  const { data: enrollmentData } = useEnrollment(campaignId, commonData.userDetails?.id || '', authKey);
  const enrollment = enrollmentData;

  // fetch my submission
  const mySubmission = useMemo(() => {
    if (!task?.is_submitted || isEmpty(submissions) || isEmpty(commonData.userDetails)) return undefined;

    return submissions.find((submission) => submission.user.id === commonData.userDetails.id);
  }, [task, submissions, commonData.userDetails]);

  // revalidation data after submit
  const submitHandler = useCallback(() => {
    taskMutate();
    submissionsMutate();
  }, [taskMutate, submissionsMutate]);


  // determine whether to show any banner (input OR status messages)
  const shouldShowAnyBanner = useMemo(() => {
    if (isEmpty(campaign) || isEmpty(task)) return false;
    
    // Show for status messages (approved, expired, team required, prerequisite, submitted pending)
    const isExpired = task.expired ? new Date(task.expired).getTime() < Date.now() : false;
    const hasStatusToShow = task.is_approved || isExpired || task.is_require_team || !!task.prerequisite_task || (task.is_submitted && !task.is_approved);
    
    // Show for input when conditions are met
    const shouldShowInput = campaign.is_joined && task.is_active && availableType;
    
    return hasStatusToShow || shouldShowInput;
  }, [campaign, task, availableType]);

  // Handle image load error
  const handleImageError = () => {
    setImageError(true);
  };

  // Handle chat modal
  const handleChatOpen = () => {
    setIsChatModalOpen(true);
  };

  const handleChatClose = () => {
    setIsChatModalOpen(false);
  };

  // Show skeleton loading state if data is loading
  if (isTaskLoading || !task || isEmpty(task)) {
    return <TaskDetailSkeleton />;
  }

  return (
    <>
    <div className="task-container">
      <Helmet>
        <meta charSet="utf-8" />
        <title>{task.name}</title>
        <meta name="description" content={task.description} />

        <meta property="og:type" content="website" />
        <meta property="og:title" content={task.name} />
        <meta property="og:description" content={task.description} />
        <meta property="og:site_name" content="Goodmates" />
        <meta property="og:image" content={task.image_small_url} />
      </Helmet>

      <FadeInWrapper delay={100}>
        <div className={`task-banner ${task?.dify_url ? 'has-chat' : ''}`}>
          <div className="task-banner-info">
            {task?.dify_url ? (
              // Chat available: image+description in column 1, chat in column 2
              <>
                <div className="task-banner-content">
                  {task.image_url && !imageError && (
                    <img
                      src={task.image_url}
                      alt={task.name}
                      className="task-banner-img with-chat"
                      onError={handleImageError}
                    />
                  )}

                  <div className="task-banner-title with-chat">
                    <h1 className="task-page-title">{task.name}</h1>
                    
                    <div>
                      <div
                        className="task-page-description"
                        dangerouslySetInnerHTML={{ __html: urlify(task.description) }}
                      />
                    </div>
                    
                    {/* Show chat button only on mobile when chat URL is available - positioned after description */}
                    <div className="mobile-only" style={{ margin: '20px 0' }}>
                      <ChatButton 
                        onClick={handleChatOpen}
                        chatUrl={task.dify_url}
                        variant="task"
                        showInitialAnimation={true}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Chat iframe for desktop only */}
                <div className="desktop-only task-chat-iframe-container">
                  <ChatIframe 
                    chatUrl={task.dify_url}
                    title={`${task.name} - ${t('#Task.chat_assistant')}`}
                    className="task-chat-iframe"
                  />
                </div>
              </>
            ) : (
              // No chat: original layout - image in column 1, title+description in column 2
              <>
                {task.image_url && !imageError && (
                  <img
                    src={task.image_url}
                    alt={task.name}
                    className="task-banner-img"
                    onError={handleImageError}
                  />
                )}

                <div className="task-banner-title">
                  <h1 className="task-page-title">{task.name}</h1>
                  
                  <div>
                    <div
                      className="task-page-description"
                      dangerouslySetInnerHTML={{ __html: urlify(task.description) }}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </FadeInWrapper>


      <FadeInWrapper delay={300}>
        <div className={`task-content ${shouldShowAnyBanner ? 'with-submission-banner' : ''}`}>
          <SubmissionList submissions={submissions || []} mySubmission={mySubmission} />

          {!availableType && !isTaskLoading && !isSubmissionsLoading && <Download />}
        </div>
      </FadeInWrapper>

      {shouldShowAnyBanner && (
        <SubmissionBanner task={task} campaign={campaign} enrollment={enrollment} submitHandler={submitHandler} />
      )}

    </div>
    
    {/* Chat modal */}
    {task?.dify_url && (
      <ChatModal
        isOpen={isChatModalOpen}
        closeHandler={handleChatClose}
        chatUrl={task.dify_url}
        title={`${task.name} - ${t('#Task.chat_assistant')}`}
      />
    )}
    </>
  );
}

export default Task;